import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import type { CustomTriggerProps, OnOpenChangeArgs } from '@atlaskit/dropdown-menu/types';
import AiAgentAddIcon from '@atlaskit/icon-lab/core/ai-agent-add';

import { ANALYTICS_SOURCE, SHARE_CONTEXT_ACTION_SUBJECT_ID } from '../../analytics.ts';
import { ShareContextMenuItems } from '../share-context-menu/ShareContextMenuItems.tsx';
import { useTwgAnalyticsEvents } from '../../useTwgAnalyticsEvents.ts';

const i18n = defineMessages({
	shareWithAgent: {
		id: 'byline-connections.preview-panel.header.share-with-agent',
		defaultMessage: 'Share with agent',
		description:
			"Button label in the Teamwork Graph preview panel header and modal top bar. Opens options to share this content's context with an artificial intelligence agent.",
	},
});

export type TeamworkGraphShareContextButtonProps = {
	contentId?: string;
	testId?: string;
	/** Forwarded to the menu's Rovo item. Omitting it drops the item. */
	onAskRovo?: (prompt: string) => void;
};

/**
 * "Share with agent": a menu of AI surfaces to send this content's context to, plus Copy as
 * prompt. Rendered to its parent, like the preview panel's more menu, so it stays inside the
 * Teamwork Graph modal's focus lock.
 */
export function TeamworkGraphShareContextButton({
	contentId,
	testId = 'teamwork-graph-modal-share-context',
	onAskRovo,
}: TeamworkGraphShareContextButtonProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();

	// Reported on open only, so the event still counts one per offer to share, as it did when
	// each click opened the modal. Closing the menu, by any route, is not a share intent.
	const handleOpenChange = useCallback(
		({ isOpen }: OnOpenChangeArgs) => {
			if (!isOpen) {
				return;
			}
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: SHARE_CONTEXT_ACTION_SUBJECT_ID,
					source: ANALYTICS_SOURCE,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	return (
		<DropdownMenu<HTMLButtonElement>
			onOpenChange={handleOpenChange}
			placement="bottom-end"
			shouldRenderToParent
			testId={`${testId}-menu`}
			trigger={({ triggerRef, ...triggerProps }: CustomTriggerProps<HTMLButtonElement>) => (
				<Button
					{...triggerProps}
					appearance="default"
					iconBefore={AiAgentAddIcon}
					ref={triggerRef}
					testId={testId}
				>
					{formatMessage(i18n.shareWithAgent)}
				</Button>
			)}
		>
			<ShareContextMenuItems contentId={contentId} onAskRovo={onAskRovo} />
		</DropdownMenu>
	);
}
